import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  deleteAllFilterPreferences,
  getViewPreferences,
  saveViewPreferences,
  ViewPreferencesQueryKeys,
} from "@/api/view-preferences/viewPreferences.queries";
import type {
  ISaveViewPreferencesPayload,
  IViewPreferencesResponse,
} from "@/api/view-preferences/viewPreferences.queries.types";

export const useGetViewPreferences = ({
  viewName,
  enabled = true,
}: {
  viewName: string;
  enabled?: boolean;
}) => {
  return useQuery({
    queryKey: [ViewPreferencesQueryKeys.get, viewName],
    queryFn: () => getViewPreferences(viewName),
    enabled,
    retry: false,
    refetchOnWindowFocus: false,
    // Always refetch on mount so navigating back fetches the latest saved
    // preferences rather than serving a stale 60s cache hit. The global
    // staleTime: 60s would otherwise skip the refetch and serve old prefs.
    staleTime: 0,
  });
};

export const useSaveViewPreferences = (viewName: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: ISaveViewPreferencesPayload) => saveViewPreferences(viewName, payload),
    onSuccess: (_data, variables) => {
      queryClient.setQueryData<IViewPreferencesResponse | null>(
        [ViewPreferencesQueryKeys.get, viewName],
        (old) => ({
          viewName,
          updatedAt: new Date().toISOString(),
          ...(old ?? {}),
          queryString: variables.queryString,
        }),
      );
    },
  });
};

export const useDeleteAllFilterPreferences = () => {
  return useMutation({
    mutationFn: () => deleteAllFilterPreferences(),
  });
};
